import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { Info, CheckCircle2, ChevronRight, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import FullProgramCard from "@/components/FullProgramCard";
import { goToOffers } from "@/lib/offers";

const statusColor = {
  "prévue": "text-obsidian/50",
  "réalisée": "text-[#4A6B53]",
  "partielle": "text-[#8C6B16]",
  "manquée": "text-destructive",
};

export default function Program() {
  const [prog, setProg] = useState(undefined);
  const [sessions, setSessions] = useState([]);
  const [showTrace, setShowTrace] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/program").then((r) => setProg(r.data));
    api.get("/sessions").then((r) => setSessions(r.data));
  }, []);

  const [unlockMsg, setUnlockMsg] = useState(null);
  const unlockNext = async () => {
    if (unlocking) return;
    setUnlocking(true); setUnlockMsg(null);
    // Des séances verrouillées existent encore -> l'achat déverrouille la suite du cycle (pas un renouvellement).
    const hasLocked = sessions.some((s) => s.locked);
    try {
      const r = await api.post("/program/renew");
      if (r.data.regenerated) { const s = await api.get("/sessions"); setSessions(s.data); toast.success("Cycle suivant débloqué"); setUnlocking(false); return; }
      // Paiement requis -> page de vente (comparaison). Aucun checkout direct, aucune offre présélectionnée.
      goToOffers(navigate, { source: "fin_cycle", returnTo: "/programme", renewal: !hasLocked });
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 403) { goToOffers(navigate, { source: "acces_expire", returnTo: "/programme", renewal: false }); return; }
      if (st === 409) setUnlockMsg({ kind: "blocked", text: detail || "Génération bloquée par sécurité. Ton coach est alerté." });
      else setUnlockMsg({ kind: "retry", text: detail || "Génération momentanément indisponible. Ton programme actuel est conservé, réessaie dans un instant." });
      setUnlocking(false);
    }
  };

  if (prog === undefined) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;

  if (!prog) return (
    <ClientLayout>
      <div className="pt-16 text-center">
        <h1 className="text-3xl font-light tracking-tight">Pas encore de programme</h1>
        <p className="mt-3 text-obsidian/60">Active ton accompagnement pour générer ta programmation.</p>
        <button onClick={() => navigate("/app")} className="mt-6 rounded-full border border-obsidian px-5 py-3 text-sm font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">Retour</button>
      </div>
    </ClientLayout>
  );

  const done = sessions.filter((s) => s.status === "réalisée").length;
  const total = sessions.length || 1;
  const pct = Math.round((done / total) * 100);

  return (
    <ClientLayout>
      <div className="fade-up">
        <ProvenanceBadge type="analyse">PROGRAMMATION IA</ProvenanceBadge>
        <h1 className="mt-5 text-3xl sm:text-4xl font-light tracking-tight capitalize">{prog.strategy?.priority || prog.domain}</h1>

        <div className="mt-6"><FullProgramCard /></div>

        <div className="mt-6 rounded-xl border border-border bg-white p-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-obsidian/50">Cycle actuel · {prog.cycle_weeks} semaines</span>
            <span className="font-medium tabnum">{pct}% terminé</span>
          </div>
          <div className="mt-3 h-px bg-border relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-sage" style={{ width: `${pct}%`, height: 2, top: -0.5 }} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <Stat n={prog.strategy?.frequency} l="Fréquence" />
            <Stat n={`${prog.strategy?.max_duration_min}′`} l="Durée max" />
            <Stat n={done + "/" + total} l="Séances" />
          </div>
        </div>

        {prog.weeks && prog.weeks.length > 1 && (
          <div className="mt-8">
            <h2 className="text-xl font-medium">Cycle · {prog.cycle_weeks} semaines</h2>
            <p className="mt-1 text-sm text-obsidian/50">Mise en place, progression, puis semaine d'allègement avant l'objectif.</p>
            <div className="mt-4 space-y-2">
              {prog.weeks.map((w) => (
                <div key={w.week} data-testid={`cycle-week-${w.week}`} className="rounded-xl border border-border bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{w.label}</span>
                    {w.taper
                      ? <span className="text-[10px] tracking-[0.12em] uppercase font-semibold rounded-full bg-[#FBF0DC] text-[#8C6B16] border border-[#EAD8B0] px-2.5 py-1">Affûtage</span>
                      : w.deload
                        ? <span className="text-[10px] tracking-[0.12em] uppercase font-semibold rounded-full bg-[#F0F4F1] text-[#4A6B53] border border-[#D5E3D8] px-2.5 py-1">Allègement</span>
                        : <span className="text-[10px] tracking-[0.12em] uppercase font-semibold text-obsidian/40">{w.sessions?.length} séances</span>}
                  </div>
                  <p className="mt-1.5 text-xs text-obsidian/55 leading-relaxed">{w.note}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="mt-10 text-xl font-medium">Mes séances</h2>
        <p className="mt-1 text-sm text-obsidian/50">Tu enchaînes les semaines automatiquement, de la mise en place à l'allègement.</p>
        {(() => {
          // Regroupe par cycle puis par semaine (les cycles ne doivent jamais fusionner).
          const cycles = [];
          sessions.forEach((s) => {
            const cyc = s.cycle_number ?? 1;
            let cg = cycles.find((x) => x.cycle === cyc);
            if (!cg) { cg = { cycle: cyc, weeks: [] }; cycles.push(cg); }
            const key = s.week ?? 1;
            let g = cg.weeks.find((x) => x.week === key);
            if (!g) { g = { week: key, label: s.week_label, deload: s.deload, taper: s.taper, note: s.week_note, items: [] }; cg.weeks.push(g); }
            g.items.push(s);
          });
          cycles.sort((a, b) => a.cycle - b.cycle);
          const multiCycle = cycles.length > 1;
          return cycles.map((cg) => (
            <div key={`cycle-${cg.cycle}`} data-testid={`cycle-block-${cg.cycle}`}>
              {multiCycle && (
                <div className="mt-8 flex items-center gap-3">
                  <span className="text-xs tracking-[0.16em] uppercase font-semibold text-sage">Cycle {cg.cycle}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              {cg.weeks.map((g) => (
                <div key={`${cg.cycle}-${g.week}`} className="mt-6" data-testid={`week-group-${cg.cycle}-${g.week}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{g.label || `Semaine ${g.week}`}</span>
                    {g.taper
                      ? <span className="text-[10px] tracking-[0.12em] uppercase font-semibold rounded-full bg-[#FBF0DC] text-[#8C6B16] border border-[#EAD8B0] px-2.5 py-1">Affûtage</span>
                      : g.deload
                        ? <span className="text-[10px] tracking-[0.12em] uppercase font-semibold rounded-full bg-[#F0F4F1] text-[#4A6B53] border border-[#D5E3D8] px-2.5 py-1">Allègement</span>
                        : null}
                  </div>
                  {g.note && <p className="mt-1 text-xs text-obsidian/55 leading-relaxed">{g.note}</p>}
                  <div className="mt-3 space-y-3">
                    {g.items.map((s) => (
                      s.locked ? (
                        <div key={s.session_id} data-testid={`program-session-locked-${s.index}`}
                          className="relative w-full text-left rounded-2xl border border-dashed border-border bg-white p-5 min-h-[76px] flex items-center justify-between gap-3 overflow-hidden">
                          <div className="min-w-0">
                            <div className="text-base font-medium blur-[3px] select-none truncate" aria-hidden="true">{s.title || "Séance"}</div>
                            <div className="mt-1 text-sm text-obsidian/50 blur-[3px] select-none" aria-hidden="true">{s.day} · {s.duration_min} min · {s.exercises_count || 3} exercices</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center"><Lock size={16} className="text-obsidian/40" /></span>
                          </div>
                        </div>
                      ) : (
                      <button key={s.session_id} data-testid={`program-session-${s.index}`}
                        onClick={() => s.status === "prévue" && navigate(`/seance/${s.session_id}`)}
                        className="w-full text-left rounded-2xl border border-border bg-white p-5 min-h-[76px] flex items-center justify-between gap-3 hover:-translate-y-0.5 active:scale-[0.99] transition-transform">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-medium">{s.title}</span>
                            {s.adapted && <ProvenanceBadge type="adaptation" />}
                          </div>
                          <div className="mt-1 text-sm text-obsidian/50">{s.day} · {s.duration_min} min · {s.exercises?.length} exercices</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-xs sm:text-sm font-semibold ${statusColor[s.status]}`}>{s.status}</span>
                          {s.status === "prévue" && <ChevronRight size={20} className="text-obsidian/30" />}
                          {s.status === "réalisée" && <CheckCircle2 size={20} className="text-[#4A6B53]" />}
                        </div>
                      </button>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ));
        })()}

        {sessions.some((s) => s.locked) && (
          <div data-testid="unlock-next-cycle" className="mt-8 rounded-2xl bg-obsidian text-white p-6">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0"><Lock size={18} className="text-sage" /></span>
              <div className="flex-1">
                <div className="text-base font-medium">Suite du programme verrouillée</div>
                <p className="mt-1 text-sm text-white/70">Certaines séances ne sont pas encore accessibles. Débloque la suite pour continuer ta progression.</p>
                <button data-testid="unlock-next-cycle-button" onClick={unlockNext} disabled={unlocking}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-obsidian px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                  {unlocking ? <Loader2 size={15} className="animate-spin" /> : <ChevronRight size={15} />} Débloquer la suite
                </button>
                {unlockMsg && (
                  <p data-testid={unlockMsg.kind === "blocked" ? "renew-blocked" : "renew-retry"} className="mt-3 text-sm text-white/85">{unlockMsg.text}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <button onClick={() => setShowTrace(!showTrace)} className="mt-8 inline-flex items-center gap-2 text-sm text-obsidian/60 hover:text-obsidian">
          <Info size={15} /> Pourquoi ce programme ?
        </button>
        {showTrace && (
          <div className="mt-4 rounded-xl border border-border bg-white p-6 space-y-4 fade-up">
            <div>
              <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">Raisons</div>
              <ul className="mt-2 space-y-1.5">{(prog.reasons || []).map((r, i) => <li key={i} className="text-sm text-obsidian/80 flex gap-2"><span className="text-sage">—</span>{r}</li>)}</ul>
            </div>
            <div>
              <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">Règles appliquées</div>
              <div className="mt-2 flex flex-wrap gap-1.5">{(prog.rules_applied || []).map((r) => <span key={r} className="font-mono text-[11px] rounded bg-secondary px-2 py-1">{r}</span>)}</div>
            </div>
            <div>
              <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">Vérification automatique</div>
              <ul className="mt-2 space-y-1">{(prog.verification?.checks || []).map((c, i) => <li key={i} className="text-sm text-obsidian/70">✓ {c}</li>)}</ul>
            </div>
            <p className="text-xs text-obsidian/40 border-t border-border pt-3">Méthode v{prog.method_version} · Données de démonstration non validées scientifiquement.</p>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}

function Stat({ n, l }) {
  return (<div><div className="font-display text-lg font-medium tabnum">{n}</div><div className="text-[11px] text-obsidian/50 mt-0.5">{l}</div></div>);
}
